import React from 'react'
import { useNavigate } from 'react-router-dom'
import SafeIcon from '../common/SafeIcon'
import { FiArrowLeft } from 'react-icons/fi'

const TermsOfService = () => {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            <span>Back</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Terms of Service</h1>
          
          <div className="text-sm text-gray-600 mb-8">
            <p><strong>Effective Date:</strong> 21/08/25</p>
            <p><strong>Last Updated:</strong> 21/08/25</p>
          </div>

          <div className="prose prose-gray max-w-none space-y-6">
            <p>
              These Terms of Service ("Terms") govern your use of the NuPitch Slide Deck platform, including all related services, features, and applications made available at <a href="https://usenupitch.com" className="text-blue-600 hover:text-blue-800">https://usenupitch.com</a> and <a href="https://deck.usenupitch.com" className="text-blue-600 hover:text-blue-800">https://deck.usenupitch.com</a> (collectively, the "Service").
            </p>
            
            <p>
              The Service is operated by Nu Pitch Ltd., a company registered in Northern Ireland under Company Number NI697170, with its registered office at 4 Church Brae, Altnagevlin, BT47 3QG ("NuPitch", "we", "us", or "our").
            </p>
            
            <p>
              By creating an account, accessing, or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, you must not use the Service.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Eligibility</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>You must be at least 18 years old, or the age of majority in your jurisdiction, to use the Service.</li>
              <li>If you are using the Service on behalf of a company or organization, you confirm that you have the authority to bind that entity to these Terms.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. The Service</h2>
            <p>NuPitch provides a cloud-based platform for developing, editing, and publishing creative slide decks related to film, TV, and media production.</p>
            <p>Key features include:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>Creating slide decks from prompts, descriptions, or uploaded scripts.</li>
              <li>Adding and editing text, images, video, and other slide elements.</li>
              <li>Using third-party AI integrations (e.g., fal.ai and OpenRouter) to generate text and images.</li>
              <li>Publishing decks publicly, optionally with password protection.</li>
              <li>Sharing profile pages that may include contact details, social media links, videos, and published decks.</li>
            </ul>
            <p>The Service is provided "as is" and "as available" without warranty of uninterrupted or error-free access.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. User Accounts</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>You must provide accurate and complete information when registering.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You are fully responsible for all activities conducted through your account.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. User Content and Ownership</h2>
            <ul className="list-disc list-inside space-y-2">
              <li><strong>Ownership:</strong> You retain full ownership of the content you create, upload, or generate using the Service ("User Content"). NuPitch does not claim ownership rights.</li>
              <li><strong>License to NuPitch:</strong> By using the Service, you grant NuPitch a limited, non-exclusive, worldwide license to host, process, and display your User Content solely to provide the Service.</li>
              <li><strong>Third-Party AI:</strong> You acknowledge that when you use AI-powered features, your input (prompts, scripts, text, or images) may be transmitted to third-party providers (e.g., fal.ai, OpenRouter) for processing. We do not control and are not responsible for the handling of such data by those providers.</li>
              <li><strong>Public Sharing:</strong> If you choose to publish or share your slide decks or profile, they may be publicly accessible on the internet. NuPitch is not responsible for unauthorized access if you share links or disable password protection.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>Upload unlawful, defamatory, infringing, or harmful content.</li>
              <li>Infringe upon third-party intellectual property rights.</li>
              <li>Attempt to gain unauthorized access to the Service or disrupt its functionality.</li>
              <li>Use AI features to generate or share illegal, harmful, or misleading material.</li>
            </ul>
            <p>NuPitch reserves the right to remove any content or suspend accounts that violate these Terms.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Security and Responsibility</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>NuPitch takes reasonable measures to secure your data, including the use of third-party infrastructure (e.g., Supabase for databases).</li>
              <li>However, you acknowledge that no system is completely secure, and uploading content online carries inherent risks of unauthorized access or disclosure.</li>
              <li>You are responsible for ensuring you have backup copies of your User Content.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Payments and Subscriptions</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>Certain features may require payment or subscription. Pricing and terms will be made available at the point of purchase.</li>
              <li>Payments are processed securely via third-party providers. NuPitch does not store full payment card details.</li>
              <li>Fees are non-refundable except as required by law.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">8. Third-Party Services</h2>
            <p>The Service integrates with third-party services (e.g., fal.ai, OpenRouter, Supabase).</p>
            <ul className="list-disc list-inside space-y-2">
              <li>NuPitch is not responsible for the availability, security, or data handling of third-party services.</li>
              <li>Your use of third-party services is subject to their terms and policies.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">9. Intellectual Property (NuPitch)</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>The Service, including software, design, and branding, is owned by or licensed to Nu Pitch Ltd.</li>
              <li>Except for your User Content, all rights are reserved. You may not copy, modify, distribute, or create derivative works without prior written consent.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">10. Disclaimers</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>The Service is provided on an "as is" and "as available" basis.</li>
              <li>NuPitch makes no warranties regarding accuracy, reliability, or suitability for your purposes.</li>
              <li>Generated content from AI may contain errors, biases, or inaccuracies, and is provided without guarantee.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">11. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>NuPitch shall not be liable for indirect, incidental, or consequential damages, including loss of profits, data, or reputation.</li>
              <li>Our total liability for any claim related to the Service shall not exceed the amount you paid us in the past 12 months.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">12. Data Protection and Privacy</h2>
            <p>Our handling of personal data is described in our Privacy Policy, which forms part of these Terms. By using the Service, you agree to our data practices.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">13. Termination</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>You may stop using the Service at any time.</li>
              <li>We may suspend or terminate access if you violate these Terms, misuse the Service, or for security/legal reasons.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">14. Governing Law and Jurisdiction</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>These Terms are governed by and construed in accordance with the laws of Northern Ireland.</li>
              <li>If you are located in the European Union, you may also benefit from consumer protections under your local laws.</li>
              <li>Any disputes shall be subject to the exclusive jurisdiction of the courts of Northern Ireland.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">15. Amendments</h2>
            <p>NuPitch may update these Terms periodically. We will notify users of significant changes by posting updates on our website. Continued use of the Service after changes constitutes acceptance.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">16. Contact Information</h2>
            <p>If you have any questions, please contact us:</p>
            <div className="ml-4 space-y-1">
              <p>Nu Pitch Ltd</p>
              <p>Registered Office: 4 Church Brae, Altnagevlin, BT47 3QG</p>
              <p>Email: <a href="mailto:support@usenupitch.com" className="text-blue-600 hover:text-blue-800">support@usenupitch.com</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsOfService
